import React, {
  useMemo,
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';

import ReactECharts from 'echarts-for-react';

import { MS_CONFIGS } from '../../../organisms/SecufireActionLogs/SecufireActionLogs';

import {
  translateEndpoint,
  translateMicroservice,
} from '../../../../utils/logTranslations';

import './DrillDownPanel.css';

/*
 * ============================================================
 * ECHELLE
 * ============================================================
 * Le panneau .ddp-area neutralise le zoom:0.8 hérité du parent
 * (.sal-wrapper) via zoom:1.25 en CSS, afin que les coordonnées
 * de clic ECharts restent fiables quelle que soit l'échelle
 * d'affichage Windows/Chrome.
 *
 * Comme ce sous-arbre est donc rendu à l'échelle native (1.0)
 * au lieu de 0.8 comme le reste de l'UI, toutes les tailles en
 * px ci-dessous sont réduites manuellement (~x0.8 pour les
 * espacements/tailles de conteneurs, ~x0.85 pour les polices,
 * légèrement moins agressif pour rester lisible) afin de
 * conserver une cohérence visuelle avec le haut de l'interface
 * SecufireActionLogs.
 * ============================================================
 */

const ENDPOINT_ROW_H = 34; // était 42
const USER_ROW_H = 24; // était 30
const GRID_TOP = 6; // était 8
const MAX_VISIBLE_ROWS = 12;
const MOBILE_S_BREAKPOINT = 480;

const DrillDownPanel = forwardRef(
  (
    {
      mode,
      searchQuery = '',
      selectedMsSet,
      msData,
      users,

      onEndpointDrill,
      onUserDrill,

      endpointDrillData,
      userDrillData,

      onEndpointDrillClose,
      onUserDrillClose,
    },
    ref
  ) => {
    const endpointChartRef = useRef(null);
    const userChartRef = useRef(null);

    const [isMobileS, setIsMobileS] = useState(
      typeof window !== 'undefined'
        ? window.innerWidth <= MOBILE_S_BREAKPOINT
        : false
    );

    /* ============================================================
       RESPONSIVE
       ============================================================ */

    useEffect(() => {
      const handleResize = () => {
        setIsMobileS(
          window.innerWidth <= MOBILE_S_BREAKPOINT
        );
      };

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    /* ============================================================
       RESET HISTORIQUE SI LES DONNEES CHANGENT
       ============================================================ */

    const previousMsDataRef = useRef(msData);

    useEffect(() => {
      if (previousMsDataRef.current !== msData) {
        onEndpointDrillClose?.();
        onUserDrillClose?.();
      }

      previousMsDataRef.current = msData;
    }, [msData, onEndpointDrillClose, onUserDrillClose]);

    /* ============================================================
       RESET SI LE MODE CHANGE
       ============================================================ */

    const previousModeRef = useRef(mode);

    useEffect(() => {
      if (previousModeRef.current !== mode) {
        onEndpointDrillClose?.();
        onUserDrillClose?.();
      }

      previousModeRef.current = mode;
    }, [mode, onEndpointDrillClose, onUserDrillClose]);

    /* ============================================================
       ENDPOINT DATA
       ============================================================ */

    const endpointBars = useMemo(() => {
      const map = {};

      [...selectedMsSet].forEach((msKey) => {
        const ms = msData.find((m) => m.microservice === msKey);

        if (!ms) return;

        const color = MS_CONFIGS[msKey]?.color || '#64748b';

        (ms.endpoints || []).forEach((ep) => {
          const shortName = ep.endpoint.replace(/^[^.]+\./, '');

          /*
           * IMPORTANT :
           * cette clé est unique par microservice + endpoint.
           */
          const key = `${msKey}::${ep.endpoint}`;

          if (!map[key]) {
            map[key] = {
              id: key,

              /* Nom affiché */
              name: translateEndpoint(shortName, msKey),

              /* Endpoint court */
              rawName: shortName,

              /* Endpoint original complet */
              fullEndpoint: ep.endpoint,

              /* Microservice */
              microservice: msKey,

              /* Couleur */
              color,

              /* Label MS */
              msLabel: MS_CONFIGS[msKey]?.label,

              /* Nombre d'appels */
              value: 0,
            };
          }

          map[key].value += ep.usage || 0;
        });
      });

      const arr = Object.values(map).sort((a, b) => b.value - a.value);

      const max = Math.max(...arr.map((d) => d.value), 1);

      const total = arr.reduce((sum, d) => sum + d.value, 0);

      return arr.map((d) => ({
        ...d,

        pct: total > 0 ? ((d.value / total) * 100).toFixed(0) : 0,

        barPct: (d.value / max) * 100,
      }));
    }, [selectedMsSet, msData]);

    /* ============================================================
       FILTER ENDPOINT
       ============================================================ */

    const filteredEndpointBars = useMemo(() => {
      if (!searchQuery.trim()) {
        return endpointBars;
      }

      const q = searchQuery.toLowerCase();

      return endpointBars.filter(
        (d) =>
          d.name.toLowerCase().includes(q) ||
          d.rawName.toLowerCase().includes(q)
      );
    }, [endpointBars, searchQuery]);

    /* ============================================================
       ENDPOINT CHART
       ============================================================ */

    const endpointChartOption = useMemo(() => {
      if (!filteredEndpointBars.length) {
        return {};
      }

      /*
       * L'ordre réel de l'axe Y.
       */
      const reversed = [...filteredEndpointBars].reverse();

      const msPresent = [
        ...new Set(filteredEndpointBars.map((d) => d.microservice)),
      ];

      const legendData = msPresent.map((msKey) => ({
        name: MS_CONFIGS[msKey]?.label || msKey,

        itemStyle: {
          color: MS_CONFIGS[msKey]?.color || '#64748b',
        },
      }));

      const bottomPad = legendData.length > 1 ? 35 : 10; // était 44 / 12

      return {
        animation: true,

        /* PAS DE TOOLTIP */
        tooltip: {
          show: false,
        },

        /* LEGEND */
        legend: {
          show: legendData.length > 1,

          data: legendData,

          bottom: 4,
          left: 'center',

          textStyle: {
            fontSize: 9, // était 11
            color: '#64748b',
            fontWeight: 500,
          },

          itemWidth: 8, // était 10
          itemHeight: 8, // était 10
          icon: 'circle',
          itemGap: 10, // était 12
        },

        /* GRID */
        grid: {
          left: 112, // était 140
          right: 56, // était 70
          top: GRID_TOP,
          bottom: bottomPad,

          /* On garde un espace propre pour les labels. */
          containLabel: false,
        },

        /* X AXIS */
        xAxis: {
          type: 'value',

          axisLabel: {
            show: !isMobileS,
            fontSize: 9, // était 10
            color: '#94a3b8',
          },

          splitLine: {
            show: !isMobileS,

            lineStyle: {
              color: '#f1f5f9',
              type: 'dashed',
            },
          },

          axisLine: {
            show: false,
          },
        },

        /* Y AXIS */
        yAxis: {
          type: 'category',

          /*
           * IMPORTANT — CORRECTIF :
           * on utilise désormais l'ID UNIQUE (msKey::endpoint)
           * comme valeur de catégorie, et non plus le nom
           * traduit affiché.
           *
           * Pourquoi : deux endpoints différents (ou de
           * microservices différents) peuvent partager le
           * même libellé traduit après translateEndpoint().
           * Si on garde le nom traduit comme clé de l'axe,
           * un clic sur le label peut résoudre le MAUVAIS
           * endpoint (le premier trouvé avec ce nom dans le
           * tableau), ce qui provoquait l'affichage des
           * détails d'un autre histogramme que celui cliqué.
           *
           * En utilisant l'id unique comme valeur de l'axe,
           * et un axisLabel.formatter pour l'affichage humain,
           * on élimine toute ambiguïté de matching.
           */
          data: reversed.map((d) => d.id),

          axisLabel: {
            fontSize: 9, // était 11
            color: '#475569',
            fontWeight: 600,

            width: 88, // était 110
            overflow: 'break',
            lineHeight: 11, // était 13

            /* Autorise ECharts à émettre un événement de clic sur le label. */
            triggerEvent: true,

            /* Le label est lui aussi cliquable. */
            cursor: 'pointer',

            /*
             * CORRECTIF :
             * on affiche le nom traduit correspondant à l'id,
             * sans changer la valeur réelle de la catégorie
             * (qui reste l'id unique).
             */
            formatter: (id) => {
              const d = reversed.find((r) => r.id === id);

              return d ? d.name : id;
            },
          },

          axisLine: {
            show: false,
          },

          axisTick: {
            show: false,
          },
        },

        /* SERIES */
        series: [
          {
            type: 'bar',

            /* IMPORTANT : curseur main au-dessus de la barre. */
            cursor: 'pointer',

            /*
             * ZONE CLIQUABLE PLEINE LARGEUR
             * showBackground dessine un rectangle (invisible ici,
             * couleur transparente) qui occupe TOUTE la largeur
             * de la bande de catégorie, pas seulement la portion
             * colorée de la barre. C'est ce rectangle qui reçoit
             * les clics, donc on ne "déborde" plus sur la ligne
             * voisine quand on clique un peu au-dessus/en-dessous
             * d'une barre courte.
             */
            showBackground: true,

            backgroundStyle: {
              color: 'transparent',
              borderRadius: [0, 3, 3, 0],
            },

            /* Les données de la barre gardent toutes les métadonnées. */
            data: reversed.map((d) => ({
              id: d.id,

              name: d.name,

              value: d.value,

              /* endpoint court */
              endpoint: d.rawName,

              /* même donnée sous son nom d'origine */
              rawName: d.rawName,

              /* endpoint complet */
              fullEndpoint: d.fullEndpoint,

              /* microservice */
              microservice: d.microservice,

              /* couleur */
              color: d.color,

              /* libellé MS */
              msLabel: d.msLabel,
            })),

            barMaxWidth: 19, // était 24

            /*
             * Réduit : avec showBackground actif, ce n'est plus
             * cet espacement qui protège des clics croisés (le
             * fond couvre déjà toute la ligne), donc on peut le
             * resserrer pour une lecture plus dense.
             */
            barCategoryGap: '20%',

            /* LABEL DES VALEURS */
            label: {
              show: true,

              position: 'right',

              formatter: (p) => `${p.value}`,

              color: '#475569',

              fontSize: 9, // était 10

              fontWeight: 700,
            },

            /* COULEUR BARRE */
            itemStyle: {
              color: (params) => params.data?.color || '#64748b',

              borderRadius: [0, 3, 3, 0],
            },

            /* HOVER BARRE */
            emphasis: {
              itemStyle: {
                opacity: 0.85,
              },
            },
          },
        ],
      };
    }, [filteredEndpointBars, isMobileS]);

    /* ============================================================
       USERS DATA
       ============================================================ */

    const userChartData = useMemo(() => {
      const userMap = {};

      const msList = [...selectedMsSet];

      msList.forEach((msKey) => {
        const ms = msData.find((m) => m.microservice === msKey);

        if (!ms) return;

        (ms.all_users || []).forEach((u) => {
          if (!userMap[u.user_id]) {
            userMap[u.user_id] = {
              user_id: u.user_id,

              username: u.username,

              active: u.active,

              total: 0,
            };
          }

          userMap[u.user_id][msKey] =
            (userMap[u.user_id][msKey] || 0) + (u.activity_count || 0);

          userMap[u.user_id].total += u.activity_count || 0;
        });
      });

      const sorted = Object.values(userMap)
        .filter((u) => u.total > 0)
        .sort((a, b) => a.total - b.total)
        .slice(-15);

      return {
        users: sorted,
        msList,
      };
    }, [msData, selectedMsSet]);

    /* ============================================================
       FILTER USERS
       ============================================================ */

    const filteredUserChartData = useMemo(() => {
      if (!searchQuery.trim()) {
        return userChartData;
      }

      const q = searchQuery.toLowerCase();

      return {
        ...userChartData,

        users: userChartData.users.filter((u) =>
          u.username?.toLowerCase().includes(q)
        ),
      };
    }, [userChartData, searchQuery]);

    /* ============================================================
       USER CHART
       ============================================================ */

    const userChartOption = useMemo(() => {
      const { users: chartUsers, msList } = filteredUserChartData;

      if (!chartUsers.length) {
        return {};
      }

      return {
        animation: true,

        /* PAS DE TOOLTIP */
        tooltip: {
          show: false,
        },

        legend: {
          bottom: 4,
          left: 'center',

          textStyle: {
            fontSize: 9, // était 11
            color: '#64748b',
            fontWeight: 500,
          },

          itemWidth: 10, // était 12
          itemHeight: 6, // était 8
          icon: 'roundRect',
          itemGap: 10, // était 12
        },

        grid: {
          left: 72, // était 90
          right: 13, // était 16
          top: GRID_TOP,
          bottom: 38, // était 48
        },

        xAxis: {
          type: 'value',

          axisLabel: {
            show: !isMobileS,
            fontSize: 9, // était 10
            color: '#94a3b8',
          },

          splitLine: {
            show: !isMobileS,

            lineStyle: {
              color: '#f1f5f9',
              type: 'dashed',
            },
          },

          axisLine: {
            show: false,
          },
        },

        yAxis: {
          type: 'category',

          data: chartUsers.map((u) => u.username),

          axisLabel: {
            fontSize: 9, // était 11
            color: '#475569',
            fontWeight: 600,
            interval: 0,
          },

          axisLine: {
            show: false,
          },

          axisTick: {
            show: false,
          },
        },

        series: msList.map((msKey, msIndex) => {
          const cfg = MS_CONFIGS[msKey] || {};

          const color = cfg.color || '#64748b';

          return {
            name: cfg.label || translateMicroservice(msKey),

            type: 'bar',

            cursor: 'pointer',

            stack: 'total',

            /*
             * ZONE CLIQUABLE PLEINE LARGEUR
             * On active showBackground uniquement sur la
             * PREMIÈRE série de la pile : dans un bar chart
             * empilé, chaque série ajoutant un fond dupliquerait
             * le rectangle et les fonds se superposeraient.
             * Un seul fond transparent par ligne suffit pour
             * couvrir toute la bande de catégorie.
             */
            showBackground: msIndex === 0,

            backgroundStyle:
              msIndex === 0
                ? {
                    color: 'transparent',
                  }
                : undefined,

            label: {
              show: false,
            },

            emphasis: {
              focus: 'series',
            },

            itemStyle: {
              color,
            },

            barMaxWidth: 26, // était 32

            data: chartUsers.map((u) => ({
              value: u[msKey] || 0,

              userId: u.user_id,

              username: u.username,

              microservice: msKey,

              color,
            })),
          };
        }),
      };
    }, [filteredUserChartData, isMobileS]);

    /* ============================================================
       ECHARTS RESIZE
       ============================================================ */

    useEffect(() => {
      const inst = endpointChartRef.current?.getEchartsInstance?.();

      if (!inst) return;

      const id = requestAnimationFrame(() => {
        inst.resize();
      });

      return () => cancelAnimationFrame(id);
    }, [filteredEndpointBars, isMobileS]);

    useEffect(() => {
      const inst = userChartRef.current?.getEchartsInstance?.();

      if (!inst) return;

      const id = requestAnimationFrame(() => {
        inst.resize();
      });

      return () => cancelAnimationFrame(id);
    }, [filteredUserChartData, isMobileS]);

    /* ============================================================
       IMPERATIVE HANDLE
       ============================================================ */

    useImperativeHandle(
      ref,
      () => ({
        getDataURL: (opts) => {
          const activeRef = mode === 'endpoint' ? endpointChartRef : userChartRef;

          return activeRef.current?.getEchartsInstance?.()?.getDataURL(opts);
        },

        getCSVRows: () => {
          if (mode === 'endpoint') {
            return filteredEndpointBars.map((d) => ({
              endpoint: d.name,

              microservice: d.msLabel,

              appels: d.value,

              pourcentage: `${d.pct}%`,
            }));
          }

          return filteredUserChartData.users.map((u) => {
            const row = {
              utilisateur: u.username,

              total: u.total,
            };

            filteredUserChartData.msList.forEach((ms) => {
              row[MS_CONFIGS[ms]?.label || ms] = u[ms] || 0;
            });

            return row;
          });
        },
      }),
      [mode, filteredEndpointBars, filteredUserChartData]
    );

    /* ============================================================
       HISTORY PANEL
       ============================================================ */

    const renderHistoryPanel = (drillData, onClose, title, color, panelMode) => {
      if (!drillData) {
        return (
          <div className="ddp-history-panel ddp-history-panel--empty">
            <div className="ddp-empty-state">
              <div className="ddp-empty-icon">
                {panelMode === 'endpoint' ? (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#c8d2de"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2v-4M9 21H5a2 2 0 0 1-2-2v-4m0 0h18" />
                  </svg>
                ) : (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#c8d2de"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="8" r="4" />

                    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                  </svg>
                )}
              </div>

              <span className="ddp-empty-title">Aucune sélection</span>

              <span className="ddp-empty-sub">
                {panelMode === 'endpoint'
                  ? 'Cliquez sur un endpoint\npour voir son historique'
                  : 'Cliquez sur un utilisateur\npour voir son historique'}
              </span>
            </div>
          </div>
        );
      }

      const { loading, calls = [], endpoint, microservice } = drillData;

      return (
        <div
          className="ddp-history-panel"
          style={{
            '--panel-color': color,
          }}
        >
          <div className="ddp-history-header">
            <div className="ddp-history-title">
              <span
                className="ddp-history-badge"
                style={{
                  background: `${color}18`,
                  color,
                }}
              >
                {title}
              </span>

              {panelMode === 'endpoint' && endpoint && (
                <span className="ddp-history-name">
                  {translateEndpoint(endpoint, microservice || '')}
                </span>
              )}
            </div>

            <button
              type="button"
              className="ddp-history-close"
              onClick={onClose}
              aria-label="Fermer"
            >
              ✕
            </button>
          </div>

          {loading ? (
            <div className="ddp-history-loading">
              <div
                className="ddp-history-spinner"
                style={{
                  borderTopColor: color,
                }}
              />

              <span>Chargement…</span>
            </div>
          ) : calls.length === 0 ? (
            <div className="ddp-history-none">Aucun appel trouvé pour cette période</div>
          ) : (
            <ul className="ddp-calls-list">
              {calls.map((c, i) => (
                <li
                  key={c.id || `${c.timestamp}-${i}`}
                  className="ddp-call-item"
                  style={
                    panelMode === 'user'
                      ? {
                          '--panel-color': c.msColor || color,
                        }
                      : undefined
                  }
                >
                  <div className="ddp-call-row">
                    <span className="ddp-call-field-label">date</span>

                    <span className="ddp-call-field-value">
                      {new Date(c.timestamp).toLocaleString('fr-FR', {
                        day: '2-digit',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>

                  {panelMode === 'endpoint' ? (
                    <>
                      <div className="ddp-call-row">
                        <span className="ddp-call-field-label">utilisateur</span>

                        <span className="ddp-call-field-value ddp-call-field-value--user">
                          {c.username || '—'}
                        </span>
                      </div>

                      <div className="ddp-call-row">
                        <span className="ddp-call-field-label">ms</span>

                        <span
                          className="ddp-call-field-value"
                          style={{
                            color,
                          }}
                        >
                          {title || '—'}
                        </span>
                      </div>

                      {c.details && (
                        <div className="ddp-call-row">
                          <span className="ddp-call-field-label">détails</span>

                          <span
                            className="ddp-call-field-value ddp-call-field-value--detail"
                            title={c.details}
                          >
                            {c.details}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="ddp-call-row">
                        <span className="ddp-call-field-label">endpoint</span>

                        <span
                          className="ddp-call-field-value ddp-call-field-value--ep"
                          title={c.endpoint}
                        >
                          {c.endpoint
                            ? translateEndpoint(c.endpoint, c.microservice || '')
                            : '—'}
                        </span>
                      </div>

                      <div className="ddp-call-row">
                        <span className="ddp-call-field-label">ms</span>

                        <span
                          className="ddp-call-field-value"
                          style={{
                            color: c.msColor || color,
                          }}
                        >
                          {c.msLabel || '—'}
                        </span>
                      </div>

                      {c.details && (
                        <div className="ddp-call-row">
                          <span className="ddp-call-field-label">détails</span>

                          <span
                            className="ddp-call-field-value ddp-call-field-value--detail"
                            title={c.details}
                          >
                            {c.details}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    };

    /* ============================================================
       ENDPOINT MODE
       ============================================================ */

    if (mode === 'endpoint') {
      const legendCount = [
        ...new Set(filteredEndpointBars.map((d) => d.microservice)),
      ].length;

      const endpointBottomPad = legendCount > 1 ? 35 : 10; // était 44 / 12

      const endpointChartPadding = GRID_TOP + endpointBottomPad;

      return (
        <div className="ddp-area">
          <div
            className="ddp-chart-side"
            style={{
              maxHeight:
                MAX_VISIBLE_ROWS * ENDPOINT_ROW_H + endpointChartPadding + 'px',
            }}
          >
            {filteredEndpointBars.length === 0 ? (
              <div className="ddp-no-data">
                {searchQuery
                  ? `Aucun endpoint trouvé pour "${searchQuery}"`
                  : 'Aucune donnée d’endpoint'}
              </div>
            ) : (
              <div
                className="ddp-chart-fixed-height"
                style={{
                  height:
                    Math.max(
                      filteredEndpointBars.length * ENDPOINT_ROW_H +
                        endpointChartPadding,
                      160 // était 200
                    ) + 'px',
                }}
              >
                <ReactECharts
                  ref={endpointChartRef}
                  option={endpointChartOption}
                  style={{
                    height: '100%',
                    width: '100%',
                  }}
                  notMerge={true}
                  lazyUpdate={false}
                  /*
                   * CLIC ENDPOINT
                   */
                  onEvents={{
                    click: (params) => {
                      let item = null;

                      /*
                       * CLIC SUR LA BARRE (ou son fond pleine largeur)
                       */
                      if (
                        params?.componentType === 'series' &&
                        params?.seriesType === 'bar' &&
                        params?.data &&
                        typeof params.data === 'object'
                      ) {
                        item = params.data;
                      }

                      /*
                       * CLIC SUR LE LABEL Y — CORRECTIF
                       * On matche désormais par ID UNIQUE
                       * (msKey::endpoint), et non plus par le nom
                       * traduit affiché. La valeur de la catégorie
                       * de l'axe Y est maintenant l'id (voir
                       * yAxis.data plus haut) ; le nom lisible
                       * n'est qu'un habillage visuel via
                       * axisLabel.formatter. Cela évite qu'un clic
                       * résolve le mauvais endpoint quand deux
                       * endpoints partagent le même libellé traduit,
                       * ou lorsque `dataIndex` n'est pas fourni par
                       * ECharts pour un clic sur un axisLabel
                       * (ce qui est le cas la plupart du temps).
                       */
                      if (!item && params?.componentType === 'yAxis') {
                        const reversed = [...filteredEndpointBars].reverse();

                        const clickedId = params?.value ?? params?.name;

                        if (clickedId != null) {
                          item = reversed.find(
                            (d) => d.id === String(clickedId)
                          );
                        }
                      }

                      /*
                       * SECURITE
                       */
                      if (!item || typeof item !== 'object') {
                        return;
                      }

                      if (!item.id || !item.fullEndpoint || !item.microservice) {
                        return;
                      }

                      /*
                       * DRILL EXACT
                       */
                      onEndpointDrill?.({
                        /* IDENTIFIANT UNIQUE */
                        id: item.id,

                        /* ENDPOINT COMPLET */
                        fullEndpoint: item.fullEndpoint,

                        /* ENDPOINT COURT */
                        endpoint: item.rawName || item.endpoint,

                        /* MICROservice */
                        microservice: item.microservice,

                        /* COULEUR */
                        color:
                          item.color ||
                          MS_CONFIGS[item.microservice]?.color ||
                          '#be1622',

                        /* LABEL MICROservice */
                        msLabel:
                          item.msLabel ||
                          MS_CONFIGS[item.microservice]?.label ||
                          item.microservice,
                      });
                    },
                  }}
                />
              </div>
            )}
          </div>

          {renderHistoryPanel(
            endpointDrillData,
            onEndpointDrillClose,
            endpointDrillData?.msLabel,
            endpointDrillData?.color || '#be1622',
            'endpoint'
          )}
        </div>
      );
    }

    /* ============================================================
       USER MODE
       ============================================================ */

    const userChartPadding = GRID_TOP + 38; // était 48

    return (
      <div className="ddp-area">
        <div
          className="ddp-chart-side"
          style={{
            maxHeight: MAX_VISIBLE_ROWS * USER_ROW_H + userChartPadding + 'px',
          }}
        >
          {filteredUserChartData.users.length === 0 ? (
            <div className="ddp-no-data">
              {searchQuery
                ? `Aucun utilisateur trouvé pour "${searchQuery}"`
                : 'Aucun utilisateur actif pour ces microservices'}
            </div>
          ) : (
            <div
              className="ddp-chart-fixed-height"
              style={{
                height:
                  Math.max(
                    filteredUserChartData.users.length * USER_ROW_H +
                      userChartPadding,
                    160 // était 200
                  ) + 'px',
              }}
            >
              <ReactECharts
                ref={userChartRef}
                option={userChartOption}
                style={{
                  height: '100%',
                  width: '100%',
                }}
                notMerge={true}
                lazyUpdate={false}
                onEvents={{
                  click: (params) => {
                    /*
                     * On accepte uniquement le clic sur une barre
                     * (ou son fond pleine largeur).
                     */
                    if (params?.componentType !== 'series') {
                      return;
                    }

                    if (params?.seriesType !== 'bar') {
                      return;
                    }

                    const item = params?.data;

                    if (!item || typeof item !== 'object') {
                      return;
                    }

                    if (item.userId == null) {
                      return;
                    }

                    onUserDrill?.({
                      user_id: item.userId,

                      username: item.username,
                    });
                  },
                }}
              />
            </div>
          )}
        </div>

        {(() => {
          const calls = userDrillData?.calls || [];

          const msCount = {};

          calls.forEach((c) => {
            const color = c.msColor || '#be1622';

            msCount[color] = (msCount[color] || 0) + 1;
          });

          const dominantColor =
            Object.entries(msCount).sort((a, b) => b[1] - a[1])[0]?.[0] ||
            '#be1622';

          return renderHistoryPanel(
            userDrillData,
            onUserDrillClose,
            userDrillData?.username,
            dominantColor,
            'user'
          );
        })()}
      </div>
    );
  }
);

export default DrillDownPanel;